import React, { useCallback, useMemo, useState } from "react";
import {
  Dropdown,
  DropdownItem,
  TextFieldSts,
} from "staysure-component-library";

import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import {
  getStrokeColor,
  validateIdentificationNumber,
} from "@/utils/organiser.utils";
import { smoothScrollToRef } from "@/utils/scrollUtils";

interface IdentificationFieldsProps {
  identificationType: string | undefined;
  identificationNumber: string | undefined;
  identificationTypeStatus: "success" | "error" | "warning" | null;
  identificationNumberStatus: "success" | "error" | "warning" | null;
  identificationTypePlaceholder: string;
  identificationNumberPlaceholder: string;
  identificationTypeRequiredError: string;
  identificationNumberInvalidError: string;
  identificationNumberWarning: string;
  identificationTypeDNILabel?: string;
  identificationTypeNIELabel?: string;
  identificationTypePassportLabel?: string;
  brand: "staysure" | "avanti" | undefined;
  handleInputChange: (field: string, value: string) => void;
  setFocusedInputs: (fields: string[]) => void;
  getFocusedFields: (field: string) => string[];
  isTabPressed: boolean;
  errorDetailsFirstError: string | null;
  firstErrorRef: React.MutableRefObject<HTMLDivElement | null>;
  identificationTypeRef: React.RefObject<HTMLDivElement | null>;
  identificationNumberRef: React.RefObject<HTMLButtonElement | null>;
  prevFieldRef: React.RefObject<HTMLElement | null>;
  hasMounted: React.MutableRefObject<boolean>;
  Styles: Record<string, string>;
}

export default function IdentificationFields({
  identificationType,
  identificationNumber,
  identificationTypeStatus,
  identificationNumberStatus,
  identificationTypePlaceholder,
  identificationNumberPlaceholder,
  identificationTypeRequiredError,
  identificationNumberInvalidError,
  identificationNumberWarning,
  identificationTypeDNILabel,
  identificationTypeNIELabel,
  identificationTypePassportLabel,
  brand,
  handleInputChange,
  setFocusedInputs,
  getFocusedFields,
  isTabPressed,
  errorDetailsFirstError,
  firstErrorRef,
  identificationTypeRef,
  identificationNumberRef,
  prevFieldRef,
  hasMounted,
  Styles,
}: Readonly<IdentificationFieldsProps>) {
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);

  // Base array for all identification types with translated labels
  const allIdentificationTypes = useMemo(
    () => [
      {
        id: "DNI",
        label: identificationTypeDNILabel,
      },
      {
        id: "NIE",
        label: identificationTypeNIELabel,
      },
      {
        id: "PASSPORT",
        label: identificationTypePassportLabel,
      },
    ],
    [
      identificationTypeDNILabel,
      identificationTypeNIELabel,
      identificationTypePassportLabel,
    ]
  );

  // Helper function to filter identification types based on passport data (derived state)
  const getFilteredIdentificationTypes = useCallback(
    (
      identificationType: string | undefined,
      identificationNumber: string | undefined
    ) => {
      return allIdentificationTypes.filter((type) => {
        // Show PASSPORT option only if there's saved passport data
        if (type.id === "PASSPORT") {
          return identificationType === "PASSPORT" && !!identificationNumber;
        }
        // Show all other identification types
        return true;
      });
    },
    [allIdentificationTypes]
  );

  // Error and warning handling: Show error, validation error, or warning message
  const identificationNumberError = useMemo(() => {
    // Error case 1: User typing without selecting type
    if (!identificationType && identificationNumber) {
      return identificationTypeRequiredError;
    }

    // Error case 2: Invalid format validation
    const validationError = validateIdentificationNumber(
      identificationType,
      identificationNumber || "",
      identificationNumberInvalidError
    );

    if (validationError) {
      return validationError;
    }

    // Warning case: Empty field with warning status (after interaction)
    if (
      identificationNumberStatus === "warning" &&
      !identificationNumber?.trim()
    ) {
      return identificationNumberWarning || "";
    }

    return "";
  }, [
    identificationType,
    identificationNumber,
    identificationTypeRequiredError,
    identificationNumberInvalidError,
    identificationNumberWarning,
    identificationNumberStatus,
  ]);

  return (
    <>
      {/* Identification Type Dropdown */}
      <InputValidationWrapper
        className="flex flex-row mb-spacing-s"
        status={identificationTypeStatus}
        childRef={
          errorDetailsFirstError === "identificationType"
            ? firstErrorRef
            : undefined
        }
      >
        <div
          className={`${Styles.country_wrapper} ${Styles.title_dropdown} ${
            isDropdownFocused && Styles.dropdown_focused
          } flex flex-row items-start w-full h-[56px]`}
          onFocus={() => isTabPressed && setIsDropdownFocused(true)}
          onBlur={() => setIsDropdownFocused(false)}
          ref={identificationTypeRef}
          role="tab"
          tabIndex={-1}
        >
          <Dropdown
            id="identification-type-dropdown"
            defaultValue={identificationType}
            placeholderTextAndLabel={identificationTypePlaceholder}
            strokeColor={getStrokeColor(brand)}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onSelectionChange={(value: any) => {
              handleInputChange("identificationType", value);

              // Only scroll if not initial mount (similar to NationalityField pattern)
              if (hasMounted.current) {
                if (prevFieldRef.current) {
                  smoothScrollToRef(prevFieldRef, 100);
                }
              } else {
                hasMounted.current = true;
              }
            }}
            onDropdownBlur={() => {
              if (!identificationTypeStatus) {
                setFocusedInputs(getFocusedFields("identificationType"));
              }
            }}
            className={`${Styles.droplistBox} ${
              identificationTypeStatus === "warning"
                ? Styles.warningMsgClass
                : ""
            } ${
              identificationTypeStatus === "success"
                ? Styles.successMsgClass
                : ""
            } border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl`}
            placeholderLeftBeforeSelect={0}
            placeholderLeftAfterSelect={-24}
            listBoxStyle={{
              outline: "none",
              margin: "0",
              minWidth: "unset !important",
              boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
            }}
            listClassName={`${Styles.listStyle} mt-[10px] rounded-radius-s`}
            unorderedListClassName="w-full !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-xs"
          >
            {getFilteredIdentificationTypes(
              identificationType,
              identificationNumber
            ).map((type) => (
              <DropdownItem value={type.id} key={type.id}>
                <span className="w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs">
                  {type.label}
                </span>
              </DropdownItem>
            ))}
          </Dropdown>
        </div>
      </InputValidationWrapper>

      {/* Identification Number Input */}
      <button
        type="button"
        className="w-full focus-visible:outline-none"
        tabIndex={-1}
        ref={identificationNumberRef}
        onFocus={() => {
          // Only scroll if not initial mount
          if (hasMounted.current && prevFieldRef.current) {
            smoothScrollToRef(prevFieldRef, 100);
          }
        }}
        data-testid="identification-number-scroll-id"
      >
        <InputValidationWrapper
          className={`${Styles.input_wrapper} flex flex-row items-start mb-spacing-s`}
          status={identificationNumberStatus}
          childRef={
            errorDetailsFirstError === "identificationNumber"
              ? firstErrorRef
              : undefined
          }
        >
          <TextFieldSts
            label={identificationNumberPlaceholder}
            data-testid="identification-number-organiser"
            id="identification-number-organiser"
            width="100%"
            name="identificationNumber"
            maxLength={12}
            state={identificationNumberStatus ?? undefined}
            message={identificationNumberError || ""}
            value={identificationNumber || ""}
            onChange={(value) =>
              handleInputChange("identificationNumber", value)
            }
            onBlur={() =>
              setFocusedInputs(getFocusedFields("identificationNumber"))
            }
            msgCustomStyles={{ width: "auto" }}
          />
        </InputValidationWrapper>
      </button>
    </>
  );
}
